// src/utils/catalogPickLines.js — doced-8f3a61c2 (Document editor, Stage 3: groundwork, no screen)
//
// What a catalog pick does to a document's lines — one place, pure, tested. Planned in catpick-4d7b19e3
// (per-form mapping), re-planned into the Document editor. ESTIMATES ONLY for now; the invoice, job and
// recurring-invoice mappings are added with their pages (each form copies different fields today).
//
// THE ESTIMATE MAPPING is exactly what today's estimate pop-up copies when you choose a catalog item
// (QuoteFormModal handleServiceSelect): serviceId, name, description, price, type (else 'Service'),
// taxCodeId (else ''), addToCatalog: false — quantity and every other field on the line are KEPT.
// Two deliberate differences, both from the founder decisions of 2026-10-25:
//   • unit: the item's unit is copied ONLY when it is a real unit (lineUnit's rule: never blank, never
//     the placeholder "Unit"). Otherwise the line carries no unit at all — a unit left from a previous
//     pick is removed, so a line never shows another item's unit.
//   • a missing description / price from the server ("null") becomes '' / 0, as a new line starts.
// src/utils/catalogPickLines.test.js lifts the pop-up's mapping and its new-line default from
// QuoteFormModal.js and fails if either ever differs from this file.

// The one unit rule, the same as functions/utils/lineUnit.js (catalogPickLines.test.js runs both on the
// same inputs and fails if they ever disagree).
export const PLACEHOLDER_UNITS = Object.freeze(['unit']);
export function customerUnit(line) {
    const raw = line && typeof line.unit === 'string' ? line.unit.trim() : '';
    if (!raw) return '';
    if (PLACEHOLDER_UNITS.includes(raw.toLowerCase())) return '';
    return raw;
}

// The estimate pop-up's new blank line (QuoteFormModal defaultLineItem), WITHOUT itemId / lineId: those
// are minted per line by newEstimateLine.
export const ESTIMATE_DEFAULT_LINE = Object.freeze({
    serviceId: '',
    name: '',
    description: '',
    quantity: 1,
    price: 0,
    taxExempt: false,
    taxExemptionNumber: '',
    type: 'Service',
    taxCodeId: '',
    addToCatalog: false,
    selectionType: 'required',
    parentItemId: null,
});

/** A new blank estimate line, as the pop-up's makeNewLineItem: its own itemId and lineId. */
export function newEstimateLine({ generateItemId, newLine, newLineId }) {
    return newLine({ ...ESTIMATE_DEFAULT_LINE, itemId: generateItemId() }, newLineId);
}

/** One catalog item (searchCatalogPicker's answer) onto an existing estimate line. Pure. */
export function pickOntoEstimateLine(line, item) {
    const out = {
        ...line,
        serviceId: item.id,
        name: item.name,
        description: item.description == null ? '' : item.description,
        price: item.price == null ? 0 : item.price,
        type: item.type || 'Service',
        taxCodeId: item.taxCodeId || '',
        addToCatalog: false,
    };
    const unit = customerUnit(item);
    if (unit) out.unit = unit; else delete out.unit;
    return out;
}

/** "× unlink" — the pop-up's Custom Entry branch: the line keeps its words and price, loses the link. */
export function unlinkEstimateLine(line) {
    return { ...line, serviceId: '', addToCatalog: false, taxCodeId: '' };
}

/** A NEW estimate line for a picked item, with a quantity (the panel's stepper; default 1). */
export function newPickedEstimateLine(item, qty, ids) {
    const q = Number(qty);
    return { ...pickOntoEstimateLine(newEstimateLine(ids), item), quantity: Number.isFinite(q) && q > 0 ? q : 1 };
}

// A line nobody has touched: still the blank default (ids aside). A line with any typing, any pick or
// any changed setting is NOT untouched.
const IGNORED_FOR_UNTOUCHED = ['itemId', 'lineId'];
export function isUntouchedEstimateLine(line) {
    if (!line || typeof line !== 'object') return false;
    const keys = new Set([...Object.keys(ESTIMATE_DEFAULT_LINE), ...Object.keys(line)]);
    for (const k of keys) {
        if (IGNORED_FOR_UNTOUCHED.includes(k)) continue;
        if (line[k] !== ESTIMATE_DEFAULT_LINE[k]) return false;
    }
    return true;
}

/**
 * The "Add N" of the catalog panel: picked [{ item, qty }] appended as new lines. An untouched blank
 * line at the BOTTOM is removed first (approved design, 2026-10-25), so adding to a new estimate doesn't
 * leave an empty first row. Only the last line, and only when untouched; nothing else is removed.
 */
export function appendPickedEstimateLines(lines, picks, ids) {
    const list = Array.isArray(lines) ? [...lines] : [];
    if (picks.length && list.length && isUntouchedEstimateLine(list[list.length - 1])) list.pop();
    return [...list, ...picks.map(({ item, qty }) => newPickedEstimateLine(item, qty, ids))];
}
